import { PropsWithChildren, useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { useUser } from '#definitions/user';

export default function TranslationProvider({ children }: PropsWithChildren) {
  const [locale, messages] = useTranslation();

  return (
    <IntlProvider
      key={locale}
      defaultLocale={'en'}
      locale={locale}
      messages={messages}
    >
      {children}
    </IntlProvider>
  );
}

function useTranslation(): [string, Record<string, string>] {
  const user = useUser();
  const [locale, setLocale] = useState<string>('en');
  const [messages, setMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    const newLocale = user?.locale;
    if (newLocale) {
      fetchLanguages(newLocale).then((msg) => {
        if (msg != null) {
          setMessages(msg);
          setLocale(newLocale);
        } else {
          console.warn('No locale messages found.');
        }
      });
    }
  }, [user]);

  return [locale, messages];
}

type LanguageMap = Record<string, string>;

async function fetchLanguages(locale: string): Promise<LanguageMap | null> {
  try {
    const messages = await languageMap[locale]?.();
    if (messages?.default != null) {
      return messages.default as LanguageMap;
    }
    return messages;
  } catch (err) {
    console.error(err);
    return null;
  }
}

const languageMap: Record<string, () => Promise<any>> =
  accommodateShortenedLocales({
    'cs-CZ': () => import('#translations/cs-CZ.json'),
    'da-DK': () => import('#translations/da-DK.json'),
    'de-DE': () => import('#translations/de-DE.json'),
    'en-GB': () => import('#translations/en-GB.json'),
    'en-ZZ': () => import('#translations/en-ZZ.json'),
    es: () => import('#translations/es.json'),
    'fi-FI': () => import('#translations/fi-FI.json'),
    'fr-FR': () => import('#translations/fr-FR.json'),
    'hu-HU': () => import('#translations/hu-HU.json'),
    'it-IT': () => import('#translations/it-IT.json'),
    'ja-JP': () => import('#translations/ja-JP.json'),
    'ko-KR': () => import('#translations/ko-KR.json'),
    'nb-NO': () => import('#translations/nb-NO.json'),
    'nl-NL': () => import('#translations/nl-NL.json'),
    'pl-PL': () => import('#translations/pl-PL.json'),
    'pt-BR': () => import('#translations/pt-BR.json'),
    'ru-RU': () => import('#translations/ru-RU.json'),
    'sv-SE': () => import('#translations/sv-SE.json'),
    'th-TH': () => import('#translations/th-TH.json'),
    'tr-TR': () => import('#translations/tr-TR.json'),
    'uk-UA': () => import('#translations/uk-UA.json'),
    'vi-VN': () => import('#translations/vi-VN.json'),
    'zh-CN': () => import('#translations/zh-CN.json'),
    'zh-TW': () => import('#translations/zh-TW.json'),
  });

function accommodateShortenedLocales<T>(
  map: Record<string, T>,
): Record<string, T> {
  return Object.fromEntries(
    Object.entries(map).flatMap(([localeKey, loader]) => {
      if (localeKey.length <= 2) {
        return [[localeKey, loader]];
      }
      return [localeKey, localeKey.substring(0, 2)].map((k) => [k, loader]);
    }),
  );
}
